<template>
  <div class="invite-reward-flow">
    <el-card shadow="never" class="animate__animated animate__fadeInUp">
      <div class="filter-card">
        <div class="filter-bar">
          <div class="filter-group">
            <el-input
              v-model="searchOpenid"
              placeholder="搜索手机号"
              prefix-icon="el-icon-search"
              clearable
              class="filter-input"
              @keyup.enter.native="handleSearch"
            />
            <el-select
              v-model="searchFlowType"
              placeholder="类型"
              clearable
              class="filter-select"
              @change="handleSearch"
            >
              <el-option label="门槛发奖" value="grant" />
              <el-option label="直推分佣" value="direct" />
              <el-option label="间推分佣" value="indirect" />
              <el-option label="提现扣款" value="withdraw" />
            </el-select>
            <el-date-picker
              v-model="dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              :default-time="['00:00:00', '23:59:59']"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
              clearable
              class="filter-daterange"
              @change="handleSearch"
            />
            <date-range-quick v-model="dateRange" @change="handleSearch" />
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="refreshList">重置</el-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>

      <el-table
        v-else
        :data="list"
        border
        stripe
        v-loading="loading"
        element-loading-text="正在加载..."
        class="animate__animated animate__fadeIn"
        row-key="id"
      >
        <el-table-column prop="id" label="ID" width="80" align="center" />
        <el-table-column prop="openid" label="手机号" min-width="200" show-overflow-tooltip />
        <el-table-column prop="coinAmount" label="变动(金币)" width="120" align="center">
          <template slot-scope="scope">
            <span :class="amountClass(scope.row.coinAmount)">
              {{ formatAmount(scope.row.coinAmount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="约合(元)" width="100" align="center">
          <template slot-scope="scope">
            {{ toYuan(scope.row.coinAmount) }}
          </template>
        </el-table-column>
        <el-table-column prop="flowType" label="类型" width="120" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.flowType === 'grant'" type="success" size="small">门槛发奖</el-tag>
            <el-tag v-else-if="scope.row.flowType === 'direct'" type="primary" size="small">直推分佣</el-tag>
            <el-tag v-else-if="scope.row.flowType === 'indirect'" type="info" size="small">间推分佣</el-tag>
            <el-tag v-else-if="scope.row.flowType === 'withdraw'" type="warning" size="small">提现扣款</el-tag>
            <span v-else>{{ scope.row.flowType }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" min-width="240" show-overflow-tooltip />
        <el-table-column prop="createdTime" label="时间" width="180" align="center" show-overflow-tooltip />
      </el-table>

      <div v-if="!error && !loading && list.length === 0" class="empty-state">
        <el-empty :description="hasFilter ? '没有符合条件的记录' : '暂无奖励明细'" />
      </div>

      <div v-if="pagination.total > 0" class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { get } from '@/utils/http'
import { getDefaultDateRange } from '@/utils/dateRange'
import { coinsToYuanText } from '@/utils/coinMoney'
import DateRangeQuick from '@/components/DateRangeQuick.vue'

export default {
  name: 'InviteRewardFlowManagement',
  components: { DateRangeQuick },
  data() {
    return {
      list: [],
      loading: false,
      error: '',
      searchOpenid: '',
      searchFlowType: '',
      dateRange: getDefaultDateRange(),
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    hasFilter() {
      return !!(this.searchOpenid || this.searchFlowType || (this.dateRange && this.dateRange.length))
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    formatAmount(amount) {
      if (amount == null) return '-'
      return amount > 0 ? `+${amount}` : String(amount)
    },
    amountClass(amount) {
      if (amount == null) return ''
      if (amount > 0) return 'amount-plus'
      if (amount < 0) return 'amount-minus'
      return ''
    },
    toYuan(coins) {
      if (coins == null) return '-'
      return coinsToYuanText(coins)
    },
    async fetchList() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        }
        if (this.searchOpenid) params.openid = this.searchOpenid
        if (this.searchFlowType) params.flowType = this.searchFlowType
        const [start, end] = this.dateRange || []
        if (start) params.startDate = start
        if (end) params.endDate = end
        const result = await get('/admin/invite-reward-flow', params)
        const data = result.data || {}
        this.list = Array.isArray(data.records) ? data.records : []
        this.pagination.total = data.total || 0
        this.pagination.page = data.page || this.pagination.page
        this.pagination.pageSize = data.pageSize || this.pagination.pageSize
      } catch (e) {
        this.error = e && e.message ? e.message : '获取奖励明细失败'
        this.list = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchList()
    },
    handlePageChange(page) {
      this.pagination.page = page
      this.fetchList()
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.fetchList()
    },
    refreshList() {
      this.searchOpenid = ''
      this.searchFlowType = ''
      this.dateRange = getDefaultDateRange()
      this.pagination.page = 1
      this.fetchList()
    }
  }
}
</script>

<style scoped>
.filter-card {
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.filter-input {
  width: 220px;
}

.filter-select {
  width: 130px;
}

.filter-daterange {
  width: 380px;
}

.error {
  color: #f87171;
  padding: 10px;
  background-color: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 4px;
  margin-bottom: 20px;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.amount-plus {
  color: #16a34a;
  font-weight: 600;
}

.amount-minus {
  color: #ea580c;
  font-weight: 600;
}
</style>
